import cn from 'classnames'
import {
  getCurrentYearRevenueActuals,
  getCurrYearBaseFields,
  getCurYearSobActuals,
  getFieldWithSellType,
  getPrevYearRevenueActuals,
  getPrevYearSobActuals,
} from 'legacy/lut/components/widgets/public/partner360/common/reports/quarterlyViewMarketTable/helpers/fieldsNameHelper'
import {
  getFormattedPercent,
  getFormattedRevenue,
} from 'legacy/lut/components/widgets/public/partner360/helpers/report'

import { RenderWhen } from 'cng-common/components/renderWhen'
import { Translation } from 'cng-common/features/translations'
import { Tooltip } from 'core-components/library/tooltip'
import {
  IQuarterlyMarketTableRow,
  TQuarterTableRowField,
} from 'partner360/models/report/sobAnalyticsReports/iQuarterlyMarketTableRow'

import { Percent } from '../../reportsCommon/percent/Percent'
import { TRANSLATION_KEYS } from '../../translations'
import { IQuarterlyTableColumn } from '../models/iQuarterlyTableColumn'
import styles from '../QuarterlyViewMarketTable.module.scss'

interface IParams {
  currentPeriodTitle: string
  previousPeriodTitle: string
  selectedCategory: string
  sellType: string
  isFullYear: boolean
  showPartnersCount: boolean
  hidePlannedColumns?: boolean
  // group 2 and 3 current-year columns show Expected Exit
  isExpectedExit?: boolean
}

export const getQuarterlyViewMarketColumns = ({
  currentPeriodTitle,
  previousPeriodTitle,
  sellType,
  selectedCategory,
  isFullYear,
  showPartnersCount,
  hidePlannedColumns = false,
  isExpectedExit = false,
}: IParams): IQuarterlyTableColumn[] => {
  const baseFields = getCurrYearBaseFields(isExpectedExit)

  const columns: (IQuarterlyTableColumn | null)[] = [
    // group 1
    {
      field: 'commercialHwHeaderParent' as TQuarterTableRowField,
      title: selectedCategory,
      className: styles.commercialHWColumn,
      hideSettings: true,
      disableSort: true,
      titleRender: ({ column }) => (
        <div className={styles.darkBlue}>
          <Translation text={column.title} />
        </div>
      ),
    },
    {
      field: 'title',
      parentField: 'commercialHwHeaderParent',
      className: cn(styles.commercialHWColumn, styles.borderLeft),
      title: TRANSLATION_KEYS.columns.market,
      hideSettings: true,
      disableSort: true,
      titleRender: ({ column }) => {
        return <Translation htmlAvailable text={column.title} />
      },
      renderFunction: (item, column, rowAttrs) => {
        const value = (item as any)[column.field]
        const coef = rowAttrs.deep
        return (
          <div
            style={{
              paddingLeft: coef ? `${coef * 16}px` : undefined,
            }}
          >
            {value}
          </div>
        )
      },
    },
    {
      field: 'partnersCount',
      parentField: 'commercialHwHeaderParent',
      className: cn(styles.commercialHWColumn, styles.borderLeft, styles.numeric),
      title: TRANSLATION_KEYS.columns.partnersCount,
      hideSettings: true,
      disableSort: true,
      titleRender: ({ column }) => {
        return <Translation htmlAvailable text={column.title} />
      },
    },
    {
      field: getFieldWithSellType<IQuarterlyMarketTableRow>('coverageMarket', sellType),
      parentField: 'commercialHwHeaderParent',
      title: TRANSLATION_KEYS.columns.amplifyCoverage,
      hideSettings: true,
      disableSort: true,
      titleRender: ({ column }) => {
        return <Translation htmlAvailable text={column.title} />
      },
      className: cn(styles.commercialHWColumn, styles.borderLeft, styles.numeric),
      renderFunction: (item, column) => {
        return getFormattedPercent({
          percent: (item as any)[column.field],
        })
      },
    },
    // group 2
    {
      field: 'emptyHeaderParent' as TQuarterTableRowField,
      className: cn(styles.column, styles.borderLeft),
      title: TRANSLATION_KEYS.topHeader.sobColumnsHeader,
      titleRender: ({ column }) => {
        return (
          <Translation
            text={column.title}
            replaceParams={[
              {
                key: '{period}',
                value: currentPeriodTitle,
              },
            ]}
          />
        )
      },
    },
    {
      field: getFieldWithSellType<IQuarterlyMarketTableRow>('prevYearSobActual', sellType),
      parentField: 'emptyHeaderParent',
      className: cn(
        styles.currentYearColumn,
        styles.borderLeft,
        styles.numeric,
        styles.subHeaderCell
      ),
      title: TRANSLATION_KEYS.columns.prevYearSobBaseline,
      hideSettings: true,
      disableSort: true,
      titleRender: ({ column }) => {
        return (
          <Translation
            htmlAvailable
            text={column.title}
            replaceParams={[{ key: '{period}', value: previousPeriodTitle }]}
          />
        )
      },
      renderFunction: (item, column) => {
        const prevYearRevenueField = getFieldWithSellType<IQuarterlyMarketTableRow>(
          'prevYearActualsRevenue',
          sellType
        )
        const sobActualsField = getFieldWithSellType<IQuarterlyMarketTableRow>(
          'prevYearSobActual',
          sellType
        )
        const revenueValue = (item as any)[prevYearRevenueField]
        const hasNoRevenue = !revenueValue
        const hasNoSob = !(item as any)[sobActualsField]
        const hasNegativeRevenue = revenueValue ? revenueValue < 0 : false
        const hasNoData = hasNoRevenue || hasNoSob
        const hideTooltip = !hasNoRevenue && !hasNoSob && !hasNegativeRevenue
        return (
          <div className={cn({ [styles.noRevenueCell]: hasNoData || hasNegativeRevenue })}>
            <Tooltip
              disabled={hideTooltip}
              tipContent={() => (
                <>
                  <RenderWhen condition={hasNoRevenue}>
                    <Translation text={TRANSLATION_KEYS.hasNoRevenueTooltip} />
                  </RenderWhen>
                  <RenderWhen condition={hasNoSob}>
                    <Translation text={TRANSLATION_KEYS.hasNoSobTooltip} />
                  </RenderWhen>
                  <RenderWhen condition={hasNegativeRevenue}>
                    <Translation text={TRANSLATION_KEYS.negativeRevenueTooltip} />
                  </RenderWhen>
                </>
              )}
            >
              {getFormattedPercent({
                percent: (item as any)[column.field],
              })}
            </Tooltip>
          </div>
        )
      },
    },
    {
      field: getFieldWithSellType<IQuarterlyMarketTableRow>('currYearSobPlan', sellType),
      parentField: 'emptyHeaderParent',
      className: cn(
        styles.currentYearColumn,
        styles.borderLeft,
        styles.numeric,
        styles.subHeaderCell
      ),
      title: TRANSLATION_KEYS.columns.currYearSobPlan,
      hideSettings: true,
      disableSort: true,
      titleRender: ({ column }) => {
        return (
          <Translation
            htmlAvailable
            text={column.title}
            replaceParams={[
              {
                key: '{period}',
                value: currentPeriodTitle,
              },
            ]}
          />
        )
      },
      renderFunction: (item, column) => {
        return (
          <>
            {getFormattedPercent({
              percent: (item as any)[column.field],
            })}
          </>
        )
      },
    },
    {
      field: getFieldWithSellType<IQuarterlyMarketTableRow>('yoyBaseline', sellType),
      parentField: 'emptyHeaderParent',
      className: cn(
        styles.currentYearColumn,
        styles.borderLeft,
        styles.numeric,
        styles.subHeaderCell
      ),
      title: TRANSLATION_KEYS.columns.yoyBaseline,
      hideSettings: true,
      disableSort: true,
      titleRender: ({ column }) => {
        return <Translation htmlAvailable text={column.title} />
      },
      renderFunction: (item, column) => {
        const value = (item as any)[column.field]
        return (
          <span
            className={cn({
              [styles.positive]: value != null && value > 0,
              [styles.negative]: value != null && value < 0,
            })}
          >
            {getFormattedPercent({
              percent: value,
              ending: 'pp',
              hasValueSign: true,
            })}
          </span>
        )
      },
    },
    {
      field: getFieldWithSellType<IQuarterlyMarketTableRow>(baseFields.sob, sellType),
      parentField: 'emptyHeaderParent',
      className: cn(
        styles.currentYearColumn,
        styles.borderLeft,
        styles.numeric,
        styles.subHeaderCell
      ),
      title: isExpectedExit
        ? TRANSLATION_KEYS.columns.currYearSobExpectedExit
        : TRANSLATION_KEYS.columns.currYearSobActual,
      hideSettings: true,
      disableSort: true,
      titleRender: ({ column }) => {
        return (
          <Translation
            htmlAvailable
            text={column.title}
            replaceParams={[
              {
                key: '{period}',
                value: currentPeriodTitle,
              },
            ]}
          />
        )
      },
      renderFunction: (item, column) => {
        const revenueField = getFieldWithSellType<IQuarterlyMarketTableRow>(
          baseFields.revenue,
          sellType
        )

        const sobField = getFieldWithSellType<IQuarterlyMarketTableRow>(baseFields.sob, sellType)
        const revenueValue = (item as any)[revenueField]
        const hasNoRevenue = !revenueValue
        const hasNegativeRevenue = revenueValue ? revenueValue < 0 : false
        const hasNoSob = !(item as any)[sobField]
        const hideTooltip = !hasNoRevenue && !hasNoSob && !hasNegativeRevenue
        return (
          <div
            className={cn({
              [styles.noRevenueCell]: hasNoRevenue || hasNoSob || hasNegativeRevenue,
            })}
          >
            <Tooltip
              disabled={hideTooltip}
              tipContent={() => (
                <>
                  <RenderWhen condition={hasNoRevenue}>
                    <Translation text={TRANSLATION_KEYS.hasNoRevenueTooltip} />
                  </RenderWhen>
                  <RenderWhen condition={hasNoSob}>
                    <Translation text={TRANSLATION_KEYS.hasNoSobTooltip} />
                  </RenderWhen>
                  <RenderWhen condition={hasNegativeRevenue}>
                    <Translation text={TRANSLATION_KEYS.negativeRevenueTooltip} />
                  </RenderWhen>
                </>
              )}
            >
              {getFormattedPercent({
                percent: (item as any)[column.field],
              })}
            </Tooltip>
          </div>
        )
      },
    },
    {
      field: getFieldWithSellType<IQuarterlyMarketTableRow>(baseFields.yoyPP, sellType),
      parentField: 'emptyHeaderParent',
      className: cn(styles.currentYearColumn, styles.borderLeft, styles.numeric),
      title: TRANSLATION_KEYS.columns.yoyPp,
      hideSettings: true,
      disableSort: true,
      titleRender: ({ column }) => {
        return <Translation htmlAvailable text={column.title} />
      },
      renderFunction: (item, column) => {
        const fieldValue = (item as any)[column.field]
        const { currentYearRevenueActualsValue, currentYearRevenueActualsField } =
          getCurrentYearRevenueActuals(sellType, item, isExpectedExit)
        const { prevYearRevenueActualsValue, prevYearRevenueActualsField } =
          getPrevYearRevenueActuals(sellType, item)

        const hasNoCurrYearRevenue = !(item as any)[currentYearRevenueActualsField]
        const hasNoPrevYearRevenue = !(item as any)[prevYearRevenueActualsField]
        const hasNoRevenue = hasNoCurrYearRevenue || hasNoPrevYearRevenue
        const hasNegativeRevenue =
          currentYearRevenueActualsValue && prevYearRevenueActualsValue
            ? currentYearRevenueActualsValue < 0 || prevYearRevenueActualsValue < 0
            : false
        const hideTooltip = !hasNoCurrYearRevenue && !hasNoPrevYearRevenue && !hasNegativeRevenue
        return (
          <div className={cn({ [styles.noRevenueCell]: hasNoRevenue || hasNegativeRevenue })}>
            <Tooltip
              disabled={hideTooltip}
              tipContent={() => (
                <>
                  <RenderWhen condition={hasNoRevenue}>
                    <Translation text={TRANSLATION_KEYS.hasNoRevenueTooltip} />
                  </RenderWhen>
                  <RenderWhen condition={hasNegativeRevenue}>
                    <Translation text={TRANSLATION_KEYS.negativeRevenueTooltip} />
                  </RenderWhen>
                </>
              )}
            >
              <span
                className={cn({
                  [styles.positive]: fieldValue != null && fieldValue > 0,
                  [styles.negative]: fieldValue != null && fieldValue < 0,
                })}
              >
                {getFormattedPercent({
                  percent: fieldValue,
                  ending: 'pp',
                  hasValueSign: true,
                })}
              </span>
            </Tooltip>
          </div>
        )
      },
    },
    {
      field: getFieldWithSellType<IQuarterlyMarketTableRow>(baseFields.qoq, sellType),
      parentField: 'emptyHeaderParent',
      title: TRANSLATION_KEYS.columns.qoqPp,
      hideSettings: true,
      disableSort: true,
      className: cn(styles.currentYearColumn, styles.borderLeft, styles.numeric),
      titleRender: ({ column }) => {
        return <Translation htmlAvailable text={column.title} />
      },
      renderFunction: (item, column) => {
        const fieldValue = (item as any)[column.field]
        const { currentYearRevenueActualsValue } = getCurrentYearRevenueActuals(
          sellType,
          item,
          isExpectedExit
        )
        const { prevYearRevenueActualsValue } = getPrevYearRevenueActuals(sellType, item)
        const { curYearSobActualsValue } = getCurYearSobActuals(sellType, item, isExpectedExit)
        const { prevYearSobActualsValue } = getPrevYearSobActuals(sellType, item)
        const hasNoCurrYearRevenue = !currentYearRevenueActualsValue
        const hasNoPrevYearRevenue = !prevYearRevenueActualsValue
        const hasNoRevenue = hasNoCurrYearRevenue || hasNoPrevYearRevenue
        const hasNegativeRevenue =
          currentYearRevenueActualsValue && prevYearRevenueActualsValue
            ? currentYearRevenueActualsValue < 0 || prevYearRevenueActualsValue < 0
            : false

        const hasNoCurrYearSob = !curYearSobActualsValue
        const hasNoPrevYearSob = !prevYearSobActualsValue
        const hasNoSob = hasNoCurrYearSob || hasNoPrevYearSob

        const hideTooltip =
          !hasNoCurrYearRevenue && !hasNoSob && !hasNoPrevYearRevenue && !hasNegativeRevenue
        return (
          <div
            className={cn({
              [styles.noRevenueCell]: hasNoRevenue || hasNoSob || hasNegativeRevenue,
            })}
          >
            <Tooltip
              disabled={hideTooltip}
              tipContent={() => (
                <>
                  <RenderWhen condition={hasNoRevenue}>
                    <Translation text={TRANSLATION_KEYS.hasNoRevenueTooltip} />
                  </RenderWhen>
                  <RenderWhen condition={hasNoSob}>
                    <Translation text={TRANSLATION_KEYS.hasNoSobTooltip} />
                  </RenderWhen>
                  <RenderWhen condition={hasNegativeRevenue}>
                    <Translation text={TRANSLATION_KEYS.negativeRevenueTooltip} />
                  </RenderWhen>
                </>
              )}
            >
              <span
                className={cn({
                  [styles.positive]: fieldValue != null && fieldValue > 0,
                  [styles.negative]: fieldValue != null && fieldValue < 0,
                })}
              >
                {getFormattedPercent({
                  percent: fieldValue,
                  ending: 'pp',
                  hasValueSign: true,
                })}
              </span>
            </Tooltip>
          </div>
        )
      },
    },
    {
      field: getFieldWithSellType<IQuarterlyMarketTableRow>(baseFields.vsPlan, sellType),
      parentField: 'emptyHeaderParent',
      className: cn(styles.currentYearColumn, styles.borderLeft, styles.numeric),
      title: TRANSLATION_KEYS.columns.vsPlan,
      hideSettings: true,
      disableSort: true,
      titleRender: ({ column }) => {
        return <Translation htmlAvailable text={column.title} />
      },
      renderFunction: (item, column) => {
        const fieldValue = (item as any)[column.field]
        const { currentYearRevenueActualsValue } = getCurrentYearRevenueActuals(
          sellType,
          item,
          isExpectedExit
        )

        const { curYearSobActualsValue } = getCurYearSobActuals(sellType, item, isExpectedExit)

        const hasNoRevenue = !currentYearRevenueActualsValue
        const hasNegativeRevenue = currentYearRevenueActualsValue
          ? currentYearRevenueActualsValue < 0
          : false

        const hasNoSob = !curYearSobActualsValue
        const hideTooltip = !hasNoRevenue && !hasNoSob && !hasNegativeRevenue
        return (
          <div
            className={cn({
              [styles.noRevenueCell]: hasNoRevenue || hasNoSob || hasNegativeRevenue,
            })}
          >
            <Tooltip
              disabled={hideTooltip}
              tipContent={() => (
                <>
                  <RenderWhen condition={hasNoRevenue}>
                    <Translation text={TRANSLATION_KEYS.hasNoRevenueTooltip} />
                  </RenderWhen>
                  <RenderWhen condition={hasNoSob}>
                    <Translation text={TRANSLATION_KEYS.hasNoSobTooltip} />
                  </RenderWhen>
                  <RenderWhen condition={hasNegativeRevenue}>
                    <Translation text={TRANSLATION_KEYS.negativeRevenueTooltip} />
                  </RenderWhen>
                </>
              )}
            >
              <span
                className={cn({
                  [styles.positive]: fieldValue != null && fieldValue > 0,
                  [styles.negative]: fieldValue != null && fieldValue < 0,
                })}
              >
                {getFormattedPercent({
                  percent: fieldValue,
                  ending: 'pp',
                  hasValueSign: true,
                })}
              </span>
            </Tooltip>
          </div>
        )
      },
    },
    // group 3
    {
      field: 'resultsHeaderParent' as TQuarterTableRowField,
      title: TRANSLATION_KEYS.topHeader.resultsColumn,
      className: cn(styles.column, styles.borderLeft),
      titleRender: ({ column }) => (
        <Translation
          text={column.title}
          replaceParams={[
            {
              key: '{period}',
              value: currentPeriodTitle,
            },
          ]}
        />
      ),
    },
    {
      field: getFieldWithSellType<IQuarterlyMarketTableRow>('prevYearActualsRevenue', sellType),
      parentField: 'resultsHeaderParent',
      className: cn(styles.resultColumn, styles.borderLeft, styles.numeric, styles.subHeaderCell),
      width: 90,
      title: TRANSLATION_KEYS.columns.prevYearSalesActuals,
      hideSettings: true,
      disableSort: true,
      titleRender: ({ column }) => {
        return (
          <Translation
            htmlAvailable
            text={column.title}
            replaceParams={[
              {
                key: '{period}',
                value: previousPeriodTitle,
              },
              {
                key: `{netNdp}`,
                value: sellType === 'Ndp' ? sellType.toUpperCase() : sellType,
              },
            ]}
          />
        )
      },
      renderFunction: (item, column) => {
        const revenue = (item as any)[column.field]
        const hasRevenue = !!revenue
        return (
          <Tooltip
            disabled={!hasRevenue}
            tipContent={() => <>{getFormattedRevenue(revenue, true)}</>}
          >
            {getFormattedRevenue((item as any)[column.field])}
          </Tooltip>
        )
      },
    },
    hidePlannedColumns
      ? null
      : {
          // hide
          field: getFieldWithSellType<IQuarterlyMarketTableRow>('currYearPlansRevenue', sellType),
          parentField: 'resultsHeaderParent',
          className: cn(
            styles.resultColumn,
            styles.borderLeft,
            styles.numeric,
            styles.subHeaderCell
          ),
          width: 90,
          title: TRANSLATION_KEYS.columns.currYearSalesPlan,
          hideSettings: true,
          disableSort: true,
          titleRender: ({ column }) => {
            return (
              <Translation
                htmlAvailable
                text={column.title}
                replaceParams={[
                  {
                    key: '{period}',
                    value: currentPeriodTitle,
                  },
                  {
                    key: `{netNdp}`,
                    value: sellType === 'Ndp' ? sellType.toUpperCase() : sellType,
                  },
                ]}
              />
            )
          },
          renderFunction: (item, column) => {
            const revenue = (item as any)[column.field]
            const hasRevenue = !!revenue
            return (
              <Tooltip
                disabled={!hasRevenue}
                tipContent={() => <>{getFormattedRevenue(revenue, true)}</>}
              >
                {getFormattedRevenue((item as any)[column.field])}
              </Tooltip>
            )
          },
        },
    hidePlannedColumns
      ? null
      : {
          // hide
          field: getFieldWithSellType<IQuarterlyMarketTableRow>('yoyRevenue', sellType),
          parentField: 'resultsHeaderParent',
          className: cn(
            styles.resultColumn,
            styles.borderLeft,
            styles.numeric,
            styles.subHeaderCell
          ),
          width: 90,
          title: TRANSLATION_KEYS.columns.yoyRevenue,
          hideSettings: true,
          disableSort: true,
          titleRender: ({ column }) => {
            return (
              <Translation
                htmlAvailable
                text={column.title}
                replaceParams={[
                  {
                    key: '{period}',
                    value: currentPeriodTitle,
                  },
                  {
                    key: `{netNdp}`,
                    value: sellType === 'Ndp' ? sellType.toUpperCase() : sellType,
                  },
                ]}
              />
            )
          },
          renderFunction: (item, column) => {
            const fieldValue = (item as any)[column.field]
            return (
              <div
                className={cn({
                  [styles.positive]: fieldValue != null && fieldValue > 0,
                  [styles.negative]: fieldValue != null && fieldValue < 0,
                })}
              >
                {getFormattedPercent({ percent: (item as any)[column.field] })}
              </div>
            )
          },
        },
    {
      field: getFieldWithSellType<IQuarterlyMarketTableRow>(baseFields.revenue, sellType),
      parentField: 'resultsHeaderParent',
      className: cn(styles.resultColumn, styles.borderLeft, styles.numeric, styles.subHeaderCell),
      width: 90,
      title: isExpectedExit
        ? TRANSLATION_KEYS.columns.currYearExpectedExit
        : TRANSLATION_KEYS.columns.currYearActuals,
      hideSettings: true,
      disableSort: true,
      titleRender: ({ column }) => {
        return (
          <Translation
            htmlAvailable
            text={column.title}
            replaceParams={[
              {
                key: '{period}',
                value: currentPeriodTitle,
              },
              {
                key: `{netNdp}`,
                value: sellType === 'Ndp' ? sellType.toUpperCase() : sellType,
              },
            ]}
          />
        )
      },
      renderFunction: (item, column) => {
        const revenue = (item as any)[column.field]
        const hasRevenue = !!revenue
        return (
          <Tooltip
            disabled={!hasRevenue}
            tipContent={() => <>{getFormattedRevenue(revenue, true)}</>}
          >
            <span
              className={cn({
                [styles.positive]: revenue != null && revenue > 0,
                [styles.negative]: revenue != null && revenue < 0,
              })}
            >
              {getFormattedRevenue(revenue)}
            </span>
          </Tooltip>
        )
      },
    },
    hidePlannedColumns
      ? null
      : {
          // hide
          field: getFieldWithSellType<IQuarterlyMarketTableRow>(
            baseFields.salesPerformance,
            sellType
          ),
          parentField: 'resultsHeaderParent',
          className: cn(
            styles.resultColumn,
            styles.borderLeft,
            styles.numeric,
            styles.subHeaderCell
          ),
          title: TRANSLATION_KEYS.columns.salesPerf,
          width: 100,
          hideSettings: true,
          disableSort: true,
          titleRender: ({ column }) => {
            return (
              <Translation
                htmlAvailable
                text={column.title}
                replaceParams={[
                  {
                    key: '{period}',
                    value: currentPeriodTitle,
                  },
                ]}
              />
            )
          },
          renderFunction: (item, column) => {
            const fieldValue = (item as any)[column.field]
            const hasNoRevenue = !fieldValue
            return (
              <div
                className={cn({
                  [styles.noRevenueCell]: hasNoRevenue,
                })}
              >
                <Tooltip
                  disabled={!hasNoRevenue}
                  tipContent={() => <Translation text={TRANSLATION_KEYS.hasNoRevenueTooltip} />}
                >
                  <span>
                    <Percent value={fieldValue} shouldConvertValue={false} withMarker />
                  </span>
                </Tooltip>
              </div>
            )
          },
        },
    {
      field: getFieldWithSellType<IQuarterlyMarketTableRow>(baseFields.yoyGrowth, sellType),
      parentField: 'resultsHeaderParent',
      className: cn(styles.resultColumn, styles.borderLeft, styles.numeric, styles.subHeaderCell),
      width: 90,
      title: TRANSLATION_KEYS.columns.yoyGrowth,
      hideSettings: true,
      disableSort: true,
      titleRender: ({ column }) => {
        return <Translation htmlAvailable text={column.title} />
      },
      renderFunction: (item, column) => {
        const fieldValue = (item as any)[column.field]
        const { currentYearRevenueActualsValue } = getCurrentYearRevenueActuals(
          sellType,
          item,
          isExpectedExit
        )
        const { prevYearRevenueActualsValue } = getPrevYearRevenueActuals(sellType, item)

        const hasNoRevenue = !currentYearRevenueActualsValue || !prevYearRevenueActualsValue
        return (
          <div
            className={cn({
              [styles.noRevenueCell]: hasNoRevenue,
            })}
          >
            <Tooltip
              disabled={!hasNoRevenue}
              tipContent={() => <Translation text={TRANSLATION_KEYS.hasNoRevenueTooltip} />}
            >
              <span
                className={cn({
                  [styles.positive]: fieldValue != null && fieldValue > 0,
                  [styles.negative]: fieldValue != null && fieldValue < 0,
                })}
              >
                {getFormattedPercent({
                  percent: fieldValue,
                  hasValueSign: true,
                })}
              </span>
            </Tooltip>
          </div>
        )
      },
    },
    // group 4
    {
      field: 'tamHeaderParent' as TQuarterTableRowField,
      title: TRANSLATION_KEYS.topHeader.tamColumnsHeader,
      className: cn(styles.column, styles.borderLeft),
      titleRender: ({ column }) => (
        <Translation
          text={column.title}
          replaceParams={[
            {
              key: '{period}',
              value: currentPeriodTitle,
            },
          ]}
        />
      ),
    },
    {
      field: getFieldWithSellType<IQuarterlyMarketTableRow>('prevYearQuarterTamActuals', sellType),
      parentField: 'tamHeaderParent',
      className: cn(styles.resultColumn, styles.borderLeft, styles.numeric, styles.subHeaderCell),
      width: 90,
      title: TRANSLATION_KEYS.columns.prevYearQuarterTamActuals,
      hideSettings: true,
      disableSort: true,
      titleRender: ({ column }) => {
        return (
          <Translation
            htmlAvailable
            text={column.title}
            replaceParams={[
              {
                key: '{period}',
                value: previousPeriodTitle,
              },
              {
                key: `{netNdp}`,
                value: sellType === 'Ndp' ? sellType.toUpperCase() : sellType,
              },
            ]}
          />
        )
      },
      renderFunction: (item, column) => {
        return <div>{getFormattedRevenue((item as any)[column.field])}</div>
      },
    },
    hidePlannedColumns
      ? null
      : {
          field: getFieldWithSellType<IQuarterlyMarketTableRow>('currYearQuarterTamPlan', sellType),
          parentField: 'tamHeaderParent',
          className: cn(
            styles.resultColumn,
            styles.borderLeft,
            styles.numeric,
            styles.subHeaderCell
          ),
          width: 90,
          title: TRANSLATION_KEYS.columns.currYearQuarterTamPlan,
          hideSettings: true,
          disableSort: true,
          titleRender: ({ column }) => {
            return (
              <Translation
                htmlAvailable
                text={column.title}
                replaceParams={[
                  {
                    key: '{period}',
                    value: currentPeriodTitle,
                  },
                  {
                    key: `{netNdp}`,
                    value: sellType === 'Ndp' ? sellType.toUpperCase() : sellType,
                  },
                ]}
              />
            )
          },
          renderFunction: (item, column) => {
            return <div>{getFormattedRevenue((item as any)[column.field])}</div>
          },
        },
    hidePlannedColumns
      ? null
      : {
          field: getFieldWithSellType<IQuarterlyMarketTableRow>('partnerTamYoY', sellType),
          parentField: 'tamHeaderParent',
          className: cn(
            styles.resultColumn,
            styles.borderLeft,
            styles.numeric,
            styles.subHeaderCell
          ),
          width: 90,
          title: TRANSLATION_KEYS.columns.partnerTamYoy,
          hideSettings: true,
          disableSort: true,
          titleRender: ({ column }) => {
            return (
              <Translation
                htmlAvailable
                text={column.title}
                replaceParams={[
                  {
                    key: '{period}',
                    value: currentPeriodTitle,
                  },
                  {
                    key: `{netNdp}`,
                    value: sellType === 'Ndp' ? sellType.toUpperCase() : sellType,
                  },
                ]}
              />
            )
          },
          renderFunction: (item, column) => {
            return <div>{getFormattedPercent({ percent: (item as any)[column.field] })}</div>
          },
        },
    {
      field: getFieldWithSellType<IQuarterlyMarketTableRow>(baseFields.tam, sellType),
      parentField: 'tamHeaderParent',
      className: cn(styles.resultColumn, styles.borderLeft, styles.numeric, styles.subHeaderCell),
      width: 90,
      title: isExpectedExit
        ? TRANSLATION_KEYS.columns.currYearQuarterTamExpectedExit
        : TRANSLATION_KEYS.columns.currYearQuarterTamActuals,
      hideSettings: true,
      disableSort: true,
      titleRender: ({ column }) => {
        return (
          <Translation
            htmlAvailable
            text={column.title}
            replaceParams={[
              {
                key: '{period}',
                value: currentPeriodTitle,
              },
              {
                key: `{netNdp}`,
                value: sellType === 'Ndp' ? sellType.toUpperCase() : sellType,
              },
            ]}
          />
        )
      },
      renderFunction: (item, column) => {
        return <div>{getFormattedRevenue((item as any)[column.field])}</div>
      },
    },
    // TODO: next column
    {
      field: getFieldWithSellType<IQuarterlyMarketTableRow>(baseFields.tamYoY, sellType),
      parentField: 'tamHeaderParent',
      className: cn(styles.resultColumn, styles.borderLeft, styles.numeric, styles.subHeaderCell),
      width: 90,
      title: isExpectedExit
        ? TRANSLATION_KEYS.columns.partnerTamExpectedExitYoy
        : TRANSLATION_KEYS.columns.partnerTamActualsYoy,
      hideSettings: true,
      disableSort: true,
      titleRender: ({ column }) => {
        return (
          <Translation
            htmlAvailable
            text={column.title}
            replaceParams={[
              {
                key: '{period}',
                value: currentPeriodTitle,
              },
              {
                key: `{netNdp}`,
                value: sellType === 'Ndp' ? sellType.toUpperCase() : sellType,
              },
            ]}
          />
        )
      },
      renderFunction: (item, column) => {
        return <div>{getFormattedPercent({ percent: (item as any)[column.field] })}</div>
      },
    },
  ]

  const filteredColumns = columns.filter((it): it is IQuarterlyTableColumn => it !== null)

  return filteredColumns.filter((col) => {
    const isQoQ =
      col.field === getFieldWithSellType<IQuarterlyMarketTableRow>(baseFields.qoq, sellType)
    const isPartnersCount = col.field === 'partnersCount'

    if (isFullYear && isQoQ) {
      return false
    }

    if (!showPartnersCount && isPartnersCount) {
      return false
    }

    return true
  })
}
